import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import qrCodeService from '../services/qrCodeService';

export default function useQRCodes(branchId) {
  const [qrCodes, setQrCodes] = useState([]);
  const [availableTables, setAvailableTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'inactive'

  const fetchQRCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filter !== 'all' ? { status: filter } : {};
      const data = await qrCodeService.getAll(params, branchId);
      setQrCodes(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load QR codes');
      toast.error('Failed to load QR codes');
    } finally {
      setLoading(false);
    }
  }, [filter, branchId]);

  const fetchAvailableTables = useCallback(async () => {
    try {
      const data = await qrCodeService.getAvailableTables(branchId);
      setAvailableTables(data || []);
    } catch (err) {
      console.error('Failed to load available tables:', err);
    }
  }, [branchId]);

  useEffect(() => {
    fetchQRCodes();
  }, [fetchQRCodes]);

  useEffect(() => {
    fetchAvailableTables();
  }, [fetchAvailableTables]);

  const createQRCode = async (data) => {
    try {
      const result = await qrCodeService.create(data);
      toast.success('QR code generated successfully');
      await fetchQRCodes();
      await fetchAvailableTables();
      return result;
    } catch (err) {
      toast.error(err.message || 'Failed to generate QR code');
      throw err;
    }
  };

  const bulkCreate = async () => {
    try {
      const result = await qrCodeService.bulkCreate();
      toast.success(`${result.length} QR codes generated successfully`);
      await fetchQRCodes();
      await fetchAvailableTables();
      return result;
    } catch (err) {
      toast.error(err.message || 'Failed to generate QR codes');
      throw err;
    }
  };

  const toggleQRCode = async (id) => {
    try {
      const result = await qrCodeService.toggle(id);
      setQrCodes((prev) => prev.map((q) => (q.id === id ? { ...q, isActive: result.isActive } : q)));
      toast.success(result.isActive ? 'QR code activated' : 'QR code deactivated');
      return result;
    } catch (err) {
      toast.error('Failed to toggle QR code');
      throw err;
    }
  };

  const deleteQRCode = async (id) => {
    try {
      await qrCodeService.delete(id);
      setQrCodes((prev) => prev.filter((q) => q.id !== id));
      toast.success('QR code deleted');
      await fetchAvailableTables();
    } catch (err) {
      toast.error('Failed to delete QR code');
      throw err;
    }
  };

  const deleteMultiple = async (ids) => {
    try {
      await qrCodeService.deleteMultiple(ids);
      setQrCodes((prev) => prev.filter((q) => !ids.includes(q.id)));
      toast.success(`${ids.length} QR code${ids.length !== 1 ? 's' : ''} deleted`);
      await fetchAvailableTables();
    } catch (err) {
      toast.error('Failed to delete selected QR codes');
      throw err;
    }
  };

  const deleteAll = async () => {
    try {
      await qrCodeService.deleteAll();
      setQrCodes([]);
      toast.success('All QR codes deleted');
      await fetchAvailableTables();
    } catch (err) {
      toast.error('Failed to delete all QR codes');
      throw err;
    }
  };

  return {
    qrCodes,
    loading,
    error,
    filter,
    setFilter,
    createQRCode,
    bulkCreate,
    toggleQRCode,
    deleteQRCode,
    deleteMultiple,
    deleteAll,
    availableTables,
    refreshQRCodes: fetchQRCodes,
  };
}
